import React, { useRef, useState, useCallback, useMemo } from 'react';
import ReportPage from '../ReportChart/ReportPage';
import { getConditionMonitoring, editConditionMonitoring } from "services/analytics";
import useChartColors from '../ReportChart/hooks/useChartColors';
import CMEditDialog from '../ReportChart/Components/CMEditDialog';

const ConditionMonitoringReport = () => {
  const { customCaptions, siteAppID, position } = JSON.parse(sessionStorage.getItem("me")) || JSON.parse(localStorage.getItem("me"));
  const { getColorsByCategory } = useChartColors();
  const dateRangeRef = useRef(null);
  const yDomainRef = useRef(null);
  const refetchRef = useRef(null);

  // Check if user has edit access (Full only)
  const hasEditAccess = position?.analyticsAccess === "F";

  // State for CM Edit Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDataPoint, setSelectedDataPoint] = useState(null);

  // Callback to receive the refetch function from ReportChart
  const handleRefetchReady = useCallback((refetchFn) => {
    refetchRef.current = refetchFn;
  }, []);

  // Handler for data point click - opens the edit dialog (only if user has edit access)
  const handleDataPointClick = useCallback((dataPoint) => {
    if (!hasEditAccess) {
      // User doesn't have permission to edit
      return;
    }
    setSelectedDataPoint(dataPoint);
    setEditDialogOpen(true);
  }, [hasEditAccess]);

  // Handler for closing the edit dialog
  const handleCloseEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setSelectedDataPoint(null);
  }, []);

  // Handler for saving the edit
  const handleSaveEdit = useCallback(async (editData) => {
    const response = await editConditionMonitoring(editData.serviceTaskQuestionID, {
      valueNumeric: editData.valueNumeric,
      cmEditReasonID: editData.cmEditReasonID,
      otherReason: editData.otherReason,
    });

    if (!response.status) {
      throw new Error(response.data?.detail || "Failed to save edit");
    }

    // Refresh chart data after successful edit
    if (refetchRef.current) {
      refetchRef.current();
    }
  }, []);

  const chartConfig = useMemo(() => ({
    id: 'condition-monitoring',
    title: 'Condition Monitoring',
    onDataPointClick: hasEditAccess ? handleDataPointClick : undefined,
    onRefetchReady: handleRefetchReady,
    fetchData: async (params) => {
      // Store the date range from params to use it in transformData
      dateRangeRef.current = {
        startDate: params.startDate,
        endDate: params.endDate
      };

      // Return invalid filters status if modelVersionTaskQuestionID is not set
      if (!params.modelVersionTaskQuestionId) {
        return {
          status: false,
          invalidFilters: true,
          message: `Please select ${customCaptions?.question || 'Question'}.`
        };
      }
      return await getConditionMonitoring(params);
    },
    availableChartTypes: ['line'],
    defaultChartType: 'line',
    useSimplifiedTooltip: true,
    axisConfig: {
      categoryAxis: {
        dataKey: 'date',
        type: 'date'
      },
      valueAxes: [{
        label: 'Value',
        // Use calculated domain that includes all values (data + thresholds)
        domain: [0, (dataMax) => Math.max(yDomainRef.current || 0, dataMax)]
      }]
    },
    series: [
      {
        key: 'valueNumeric',
        label: 'Value',
        color: '#3b679b',
        showLegend: true,
        dynamicSeries: {
          keyField: 'siteAssetName',
          colors: getColorsByCategory('primary')
        }
      },
      {
        key: 'minValue',
        label: 'Minimum',
        color: '#d9534f',
        showLegend: true,
        strokeDasharray: '5 5',
        strokeWidth: 3,
        dot: false,
        activeDot: false
      },
      {
        key: 'maxValue',
        label: 'Maximum',
        color: '#ff9800',
        showLegend: true,
        strokeDasharray: '5 5',
        strokeWidth: 3,
        dot: false,
        activeDot: false
      }
    ],
    transformData: (response) => {
      const apiData = response.data || {};
      const questionMetadata = apiData.questionMetadata;
      const dataArray = Array.isArray(apiData.chartData) ? apiData.chartData :
                        Array.isArray(response) ? response : [];

      // If there's no data but we have metadata and a date range,
      // create points to draw min/max lines across the range.
      if (dataArray.length === 0 && questionMetadata && dateRangeRef.current?.startDate && dateRangeRef.current?.endDate) {
        const { startDate, endDate } = dateRangeRef.current;
        const { minValue, maxValue } = questionMetadata;

        return [
          { date: new Date(startDate).getTime(), minValue, maxValue },
          { date: new Date(endDate).getTime(), minValue, maxValue },
        ];
      }

      // Check if we need boundary points for single data point centering
      let boundaryPoints = [];
      if (dataArray.length === 1) {
        const minValue = questionMetadata?.minValue;
        const maxValue = questionMetadata?.maxValue;
        const dataPointTimestamp = new Date(dataArray[0].date + "Z").getTime();

        const { startDate, endDate } = dateRangeRef.current || {};

        // If we have explicit date range, use it; otherwise create padding around the data point
        if (startDate && endDate) {
          const startTimestamp = new Date(startDate).getTime();
          const endTimestamp = new Date(endDate).getTime();

          // Add boundary points at start/end of date range to ensure proper X-axis scaling
          if (startTimestamp < dataPointTimestamp) {
            boundaryPoints.push({ date: startTimestamp, minValue, maxValue });
          }
          if (endTimestamp > dataPointTimestamp) {
            boundaryPoints.push({ date: endTimestamp, minValue, maxValue });
          }
        } else {
          // No explicit date range (e.g., "Show All"), add padding around the single point
          // Use 30 days before and after to give visual context
          const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
          boundaryPoints.push({ date: dataPointTimestamp - thirtyDaysMs, minValue, maxValue });
          boundaryPoints.push({ date: dataPointTimestamp + thirtyDaysMs, minValue, maxValue });
        }
      }

      // Transform data to include defects and maintain original structure
      const transformedData = dataArray.map((item) => {
        const dateObj = new Date(item.date + "Z");

        // Format display date (for tooltips and optionally for X-axis)
        const displayDate = dateObj.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });

        // Format modified datetime if present
        let formattedModifiedDateTime = null;
        if (item.modifiedDateTime) {
          const modifiedDateObj = new Date(item.modifiedDateTime);
          formattedModifiedDateTime = modifiedDateObj.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }) + ' ' + modifiedDateObj.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
          });
        }

        const dataItem = {
          ...item,
          // Store the timestamp for X-axis positioning and sorting
          date: dateObj.getTime(),
          // Store formatted date for tooltips and text labels
          displayDate: displayDate,
          // For backward compatibility - some components may use 'label'
          label: displayDate,
          // Current and original numeric values
          valueNumeric: item.valueNumeric,
          originalValueNumeric: item.originalValueNumeric,
          // Map valueNumeric to 'value' for tooltip display (for chart compatibility)
          value: item.valueNumeric,
          defects: item.defects || [],
          siteAssetName: item.siteAssetName || item.siteAssetReference || 'Unknown',
          serviceTaskQuestionID: item.serviceTaskQuestionID,
          // Edit metadata from API
          isEdited: item.isEdited || false,
          modifiedByUserName: item.modifiedByUserName,
          modifiedDateTime: formattedModifiedDateTime,
          editReasonName: item.editReasonName,
          editOtherReason: item.editOtherReason
        };

        if (questionMetadata) {
          dataItem.minValue = questionMetadata.minValue;
          dataItem.maxValue = questionMetadata.maxValue;
        }

        return dataItem;
      });

      // Add boundary points for single data point centering (if any)
      if (boundaryPoints.length > 0) {
        transformedData.push(...boundaryPoints);
      }

      // Sort by date ascending
      transformedData.sort((a, b) => a.date - b.date);

      // Calculate Y-axis domain based on all values (data points + thresholds)
      const allValues = transformedData.flatMap(item => [
        item.valueNumeric,
        item.minValue,
        item.maxValue
      ]).filter(v => v != null && !isNaN(v));

      if (allValues.length > 0) {
        const maxVal = Math.max(...allValues);
        // Add 10% padding above max for better visibility, rounded to 1 decimal place
        yDomainRef.current = Math.round(maxVal * 1.1 * 10) / 10;
      }

      console.log('Transformed Data:', transformedData);
      return transformedData;
    },
    csvExport: {
      enabled: true,
      label: 'Export CM Data (CSV)',
      getQuestionIds: (filterParams) => {
        return filterParams.modelVersionTaskQuestionId
          ? [parseInt(filterParams.modelVersionTaskQuestionId)]
          : null;
      }
    },
    filters: {
      timeframe: {
        allowCustomRange: true,
        defaultRange: 'show-all',
      },
      department: { multiSelect: true },
      model: {
        showAll: false,
        required: true
      },
      asset: {
        show: model => model?.modelTemplateType === 'A',
        showAll: false
      },
      role: {
        showAll: true
      },
      interval: {
        showAll: true,
      },
      question: {
        showAll: false,
        required: true,
        show: true
      }
    }
  }), [customCaptions, getColorsByCategory, handleDataPointClick, handleRefetchReady, hasEditAccess]);

  return (
    <>
      <ReportPage
        title="Condition Monitoring"
        chartConfig={chartConfig}
      />

      {/* CM Edit Dialog */}
      <CMEditDialog
        open={editDialogOpen}
        closeHandler={handleCloseEditDialog}
        dataPoint={selectedDataPoint}
        onSave={handleSaveEdit}
        siteAppId={siteAppID}
      />
    </>
  );
};

export default ConditionMonitoringReport;
